<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import '@/lib/monaco-workers';
import * as monaco from 'monaco-editor';
import '@/lib/monaco-jsonc';
import { useThemeStore } from '@/stores/theme';

// Side-by-side, read-only Monaco DiffEditor. `original` renders in the left pane (typically a
// historical snapshot), `modified` in the right (the current file). Both follow the global
// Monaco JSON diagnostics (jsonc with comments allowed) and the operator's theme preference.
// The component re-mounts the underlying models on prop change so switching between snapshots
// is one `setModel` call rather than a full editor rebuild.
const props = withDefaults(
  defineProps<{ original: string; modified: string; language?: string }>(),
  { language: 'json' },
);

const host = ref<HTMLDivElement | null>(null);
const theme = useThemeStore();
let editor: monaco.editor.IStandaloneDiffEditor | null = null;
let originalModel: monaco.editor.ITextModel | null = null;
let modifiedModel: monaco.editor.ITextModel | null = null;

function applyTheme() {
  monaco.editor.setTheme(theme.resolvedDark ? 'vs-dark' : 'vs');
}

function buildModels() {
  // Each render gets fresh models so the diff view's "changed" decorations are recomputed
  // from scratch. Disposing the previous pair before assigning the new ones avoids the
  // memory leak that would otherwise pile up across snapshot switches.
  originalModel?.dispose();
  modifiedModel?.dispose();
  originalModel = monaco.editor.createModel(props.original, props.language);
  modifiedModel = monaco.editor.createModel(props.modified, props.language);
}

onMounted(() => {
  if (!host.value) return;
  applyTheme();
  editor = monaco.editor.createDiffEditor(host.value, {
    automaticLayout: true,
    readOnly: true,
    renderSideBySide: true,
    minimap: { enabled: false },
    fontSize: 13,
    scrollBeyondLastLine: false,
  });
  buildModels();
  editor.setModel({ original: originalModel!, modified: modifiedModel! });
});

watch([() => props.original, () => props.modified, () => props.language], () => {
  if (!editor) return;
  buildModels();
  editor.setModel({ original: originalModel!, modified: modifiedModel! });
});

watch(() => theme.resolvedDark, applyTheme);

onBeforeUnmount(() => {
  editor?.dispose();
  editor = null;
  originalModel?.dispose();
  modifiedModel?.dispose();
  originalModel = null;
  modifiedModel = null;
});
</script>

<template>
  <div ref="host" class="diff-viewer"></div>
</template>

<style scoped>
.diff-viewer {
  width: 100%;
  height: 100%;
  min-height: 18rem;
  border: 1px solid var(--p-content-border-color, #d1d5db);
}
</style>
